import { useEffect, useMemo, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    FormControl,
    CircularProgress,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import { adsProxy, expertModerationStatsProxy, expertSlaProxy, expertTariffsProxy } from './apiGateway';
import {
    buildEntityTitleMaps,
    buildTariffHistoryRevisionPdfBlob,
    formatTariffHistoryActorLabel,
    TariffHistoryItem,
    TariffHistoryRevision
} from './ExpertTariffsPage';
import {
    buildSlaHistoryRevisionPdfBlob,
    SlaHistoryItem,
    SlaHistoryRevision
} from './ExpertSlaPage';

type StatsMonth = {
    label: string;
    start: string;
    end: string;
    checkedAdsCount: number;
    earningsRub: number;
};

type StatsPeriod = {
    key: string;
    title: string;
    label: string;
    start: string;
    end: string;
    payoutDate: string;
    checkedAdsCount: number;
    earningsRub: number;
    isActive: boolean;
};

type StatsNextPayout = {
    date: string;
    periodKey: string;
    checkedAdsCount: number;
    earningsRub: number;
};

type StatsExpert = {
    userId: string;
    login: string;
    fullName: string;
    monthCheckedAdsCount: number;
    monthEarningsRub: number;
    monthSlaViolationsCount: number;
    periodCheckedAdsCount: number;
    periodEarningsRub: number;
};

type StatsRecentAd = {
    id: string;
    adId: string;
    decidedAt: string | null;
    moderationResult: string;
    verdictLabel: string;
    tariffRub: number;
    expertLogin: string;
    expertFullName: string;
    slaViolated?: boolean;
    slaApplicable?: boolean;
    slaDiscountPercent?: number;
};

type ExpertModerationStatsResponse = {
    selectedMonth?: string;
    tariffRub: number;
    generatedAt: string;
    month: StatsMonth;
    periods: StatsPeriod[];
    nextPayout: StatsNextPayout;
    experts: StatsExpert[];
    recentCheckedAds: StatsRecentAd[];
};

const metricCardSx = {
    borderRadius: 2,
    '& .MuiCardContent-root': {
        py: 2
    }
};

function formatMoney(value: number) {
    return `${Number(value || 0).toLocaleString('ru-RU')} ₽`;
}

function maskMoney(value: number, hidden: boolean) {
    if (hidden) {
        return '••••••';
    }
    return formatMoney(value);
}

function formatDate(value: string | null | undefined) {
    if (!value) {
        return '-';
    }
    return new Date(value).toLocaleDateString('ru-RU');
}

function formatDateTime(value: string | null | undefined) {
    if (!value) {
        return '-';
    }
    return new Date(value).toLocaleString('ru-RU');
}

function formatPercent(value: number) {
    const normalized = Number(value);
    if (!Number.isFinite(normalized)) {
        return '0';
    }
    return Number.isInteger(normalized)
        ? String(normalized)
        : normalized.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function renderSlaBadge(violated?: boolean, applicable?: boolean) {
    if (applicable === false) {
        return <Chip size="small" color="default" variant="outlined" label="Не применяется" />;
    }
    if (violated) {
        return <Chip size="small" color="error" label="Нарушен" />;
    }
    return <Chip size="small" color="success" variant="outlined" label="В норме" />;
}

function toMonthKey(date: Date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function buildMonthOptions(monthsCount = 12) {
    const now = new Date();
    return Array.from({ length: monthsCount }, (_, index) => {
        const date = new Date(now.getFullYear(), now.getMonth() - index, 1, 12, 0, 0, 0);
        return {
            value: toMonthKey(date),
            label: date.toLocaleString('ru-RU', { month: 'long', year: 'numeric' })
        };
    });
}

export const ExpertStatisticsPage = () => {
    const notify = useNotify();
    const [loading, setLoading] = useState(true);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [slaHistoryLoading, setSlaHistoryLoading] = useState(true);
    const [stats, setStats] = useState<ExpertModerationStatsResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [hideAmount, setHideAmount] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState<string>(() => toMonthKey(new Date()));
    const [tariffHistory, setTariffHistory] = useState<TariffHistoryItem[]>([]);
    const [slaHistory, setSlaHistory] = useState<SlaHistoryItem[]>([]);
    const [downloadingRevisionId, setDownloadingRevisionId] = useState<string | null>(null);
    const [downloadingSlaRevisionId, setDownloadingSlaRevisionId] = useState<string | null>(null);
    const monthOptions = useMemo(() => buildMonthOptions(), []);

    const loadEntityTitleMapsForPdf = async () => {
        const adConfigResponse = await adsProxy.getAdConfig();
        const adConfigBody = (adConfigResponse?.body ?? adConfigResponse) as {
            categoriesByGender?: Array<{
                categories?: Array<{
                    id?: string;
                    title?: string;
                    subcategories?: Array<{ id?: string; title?: string }>;
                }>;
            }>;
        };
        return buildEntityTitleMaps(Array.isArray(adConfigBody?.categoriesByGender) ? adConfigBody.categoriesByGender : []);
    };

    const loadStats = async (month: string) => {
        setLoading(true);
        setError(null);
        try {
            const response = await expertModerationStatsProxy.getStats(month);
            setStats(response as ExpertModerationStatsResponse);
        } catch (loadError) {
            console.error('Failed to load expert moderation stats:', loadError);
            setError('Не удалось загрузить статистику');
            notify('Не удалось загрузить статистику', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const loadTariffHistory = async () => {
        setHistoryLoading(true);
        try {
            const response = await expertTariffsProxy.getHistory(100);
            setTariffHistory(Array.isArray(response?.data) ? response.data as TariffHistoryItem[] : []);
        } catch (historyError) {
            console.error('Failed to load tariff history:', historyError);
            notify('Не удалось загрузить историю тарифов', { type: 'warning' });
        } finally {
            setHistoryLoading(false);
        }
    };

    const loadSlaHistory = async () => {
        setSlaHistoryLoading(true);
        try {
            const response = await expertSlaProxy.getHistory(100);
            setSlaHistory(Array.isArray(response?.data) ? response.data as SlaHistoryItem[] : []);
        } catch (historyError) {
            console.error('Failed to load SLA history:', historyError);
            notify('Не удалось загрузить историю SLA', { type: 'warning' });
        } finally {
            setSlaHistoryLoading(false);
        }
    };

    useEffect(() => {
        loadStats(selectedMonth);
    }, [selectedMonth]);

    useEffect(() => {
        loadTariffHistory();
        loadSlaHistory();
    }, []);

    const handleDownloadRevisionPdf = async (historyItem: TariffHistoryItem) => {
        const previewWindow = window.open('about:blank', '_blank');
        if (!previewWindow) {
            notify('Браузер заблокировал открытие вкладки. Разрешите pop-up.', { type: 'warning' });
            return;
        }

        previewWindow.document.write('<p>Формируем PDF...</p>');
        previewWindow.document.close();
        setDownloadingRevisionId(historyItem.id);
        try {
            const [response, entityTitleMaps] = await Promise.all([
                expertTariffsProxy.getHistoryRevision(historyItem.id),
                loadEntityTitleMapsForPdf()
            ]);
            const revision = response?.data as TariffHistoryRevision | undefined;
            if (!revision) {
                throw new Error('Не удалось получить данные ревизии');
            }

            const blob = buildTariffHistoryRevisionPdfBlob(revision, entityTitleMaps);
            const url = URL.createObjectURL(blob);
            previewWindow.location.href = url;
            window.setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 60_000);
        } catch (downloadError) {
            previewWindow.close();
            const message = downloadError instanceof Error && downloadError.message.trim()
                ? downloadError.message
                : 'Не удалось сформировать PDF';
            notify(message, { type: 'error' });
        } finally {
            setDownloadingRevisionId(null);
        }
    };

    const handleDownloadSlaRevisionPdf = async (historyItem: SlaHistoryItem) => {
        const previewWindow = window.open('about:blank', '_blank');
        if (!previewWindow) {
            notify('Браузер заблокировал открытие вкладки. Разрешите pop-up.', { type: 'warning' });
            return;
        }

        previewWindow.document.write('<p>Формируем PDF...</p>');
        previewWindow.document.close();
        setDownloadingSlaRevisionId(historyItem.id);
        try {
            const [response, entityTitleMaps] = await Promise.all([
                expertSlaProxy.getHistoryRevision(historyItem.id),
                loadEntityTitleMapsForPdf()
            ]);
            const revision = response?.data as SlaHistoryRevision | undefined;
            if (!revision) {
                throw new Error('Не удалось получить данные ревизии');
            }

            const blob = buildSlaHistoryRevisionPdfBlob(revision, entityTitleMaps);
            const url = URL.createObjectURL(blob);
            previewWindow.location.href = url;
            window.setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 60_000);
        } catch (downloadError) {
            previewWindow.close();
            const message = downloadError instanceof Error && downloadError.message.trim()
                ? downloadError.message
                : 'Не удалось сформировать PDF';
            notify(message, { type: 'error' });
        } finally {
            setDownloadingSlaRevisionId(null);
        }
    };

    const activePeriod = useMemo(
        () => stats?.periods?.find((period) => period.isActive) || null,
        [stats]
    );
    const activePeriodLabel = activePeriod?.label || '-';

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                <Title title="Статистика экспертов" />
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <CircularProgress size={20} />
                    <Typography>Загрузка статистики...</Typography>
                </Stack>
            </Box>
        );
    }

    if (error || !stats) {
        return (
            <Box sx={{ p: 2 }}>
                <Title title="Статистика экспертов" />
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error || 'Не удалось загрузить статистику'}
                </Alert>
                <Button variant="contained" onClick={() => loadStats(selectedMonth)}>
                    Повторить
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Title title="Статистика экспертов" />

            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 2, gap: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Статистика экспертов
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                        <InputLabel id="expert-stats-month-label">Месяц</InputLabel>
                        <Select
                            labelId="expert-stats-month-label"
                            value={selectedMonth}
                            label="Месяц"
                            onChange={(event) => setSelectedMonth(String(event.target.value))}
                        >
                            {monthOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={hideAmount}
                                onChange={(_, checked) => setHideAmount(checked)}
                            />
                        )}
                        label="Скрыть сумму"
                        sx={{
                            mr: 0,
                            whiteSpace: 'nowrap',
                            '& .MuiFormControlLabel-label': {
                                whiteSpace: 'nowrap'
                            }
                        }}
                    />
                </Stack>
            </Stack>

            <Card sx={{ ...metricCardSx, mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Итог за месяц: {stats.month.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Период: {formatDate(stats.month.start)} - {formatDate(stats.month.end)}
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                        <Box>
                            <Typography variant="body2" color="text.secondary">Проверено объявлений</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>{stats.month.checkedAdsCount}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">Заработано (все эксперты)</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>{maskMoney(stats.month.earningsRub, hideAmount)}</Typography>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            <Box
                sx={{
                    mb: 2,
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(280px, 1fr))' }
                }}
            >
                {stats.periods.map((period) => (
                    <Card key={period.key} sx={metricCardSx}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography variant="h6">{period.title}</Typography>
                                {period.isActive ? (
                                    <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700 }}>
                                        Активный период
                                    </Typography>
                                ) : (
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                                        (прошлый период)
                                    </Typography>
                                )}
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Период: {period.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Проверено объявлений</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{period.checkedAdsCount}</Typography>
                            <Typography variant="body2" color="text.secondary">Сумма</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>{maskMoney(period.earningsRub, hideAmount)}</Typography>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            <Card sx={{ ...metricCardSx, mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Ожидаемая ближайшая выплата
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Дата выплаты: {formatDate(stats.nextPayout.date)}
                        {activePeriod ? ` (период ${activePeriod.label})` : ''}
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                        <Box>
                            <Typography variant="body2" color="text.secondary">Тарифицируемых проверок</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>{stats.nextPayout.checkedAdsCount}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">Ожидаемая сумма</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>{maskMoney(stats.nextPayout.earningsRub, hideAmount)}</Typography>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            <Card sx={{ ...metricCardSx, mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Статистика по экспертам
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Логин</TableCell>
                                    <TableCell>ФИО</TableCell>
                                    <TableCell align="right">Проверок за месяц</TableCell>
                                    <TableCell align="right">Сумма за месяц</TableCell>
                                    <TableCell align="right">Нарушений SLA за месяц</TableCell>
                                    <TableCell align="right">
                                        Проверок за расчетный период ({activePeriodLabel})
                                    </TableCell>
                                    <TableCell align="right">
                                        Сумма за расчетный период ({activePeriodLabel})
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stats.experts.map((expert) => (
                                    <TableRow key={expert.userId || `${expert.login}-${expert.fullName}`}>
                                        <TableCell>{expert.login || '-'}</TableCell>
                                        <TableCell>{expert.fullName || '-'}</TableCell>
                                        <TableCell align="right">{expert.monthCheckedAdsCount}</TableCell>
                                        <TableCell align="right">{maskMoney(expert.monthEarningsRub, hideAmount)}</TableCell>
                                        <TableCell align="right">{expert.monthSlaViolationsCount ?? 0}</TableCell>
                                        <TableCell align="right">{expert.periodCheckedAdsCount}</TableCell>
                                        <TableCell align="right">{maskMoney(expert.periodEarningsRub, hideAmount)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            <Accordion sx={{ mb: 3 }} defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">
                        История редакций тарифов ({tariffHistory.length})
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {historyLoading ? (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <CircularProgress size={18} />
                            <Typography variant="body2">Загрузка истории...</Typography>
                        </Stack>
                    ) : tariffHistory.length === 0 ? (
                        <Alert severity="info">История пока пустая.</Alert>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Редакция от</TableCell>
                                        <TableCell>Редактор</TableCell>
                                        <TableCell align="right">Категорий</TableCell>
                                        <TableCell align="right">Подкатегорий</TableCell>
                                        <TableCell align="right">Файл</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tariffHistory.map((historyItem, index) => (
                                        <TableRow key={historyItem.id}>
                                            <TableCell>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <span>{formatDateTime(historyItem.createdAt)}</span>
                                                    {index === 0 ? (
                                                        <Chip size="small" color="success" variant="outlined" label="Актуальная" />
                                                    ) : null}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>{formatTariffHistoryActorLabel(historyItem.updatedBy)}</TableCell>
                                            <TableCell align="right">{historyItem.categoryCount ?? 0}</TableCell>
                                            <TableCell align="right">{historyItem.subcategoryCount ?? 0}</TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={downloadingRevisionId === historyItem.id ? <CircularProgress size={14} /> : <DownloadIcon />}
                                                    onClick={() => handleDownloadRevisionPdf(historyItem)}
                                                    disabled={downloadingRevisionId === historyItem.id}
                                                >
                                                    {downloadingRevisionId === historyItem.id ? 'Формирую...' : 'Скачать PDF'}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </AccordionDetails>
            </Accordion>

            <Accordion sx={{ mb: 3 }} defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">
                        История редакций SLA нарушений ({slaHistory.length})
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {slaHistoryLoading ? (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <CircularProgress size={18} />
                            <Typography variant="body2">Загрузка истории...</Typography>
                        </Stack>
                    ) : slaHistory.length === 0 ? (
                        <Alert severity="info">История пока пустая.</Alert>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Редакция от</TableCell>
                                        <TableCell>Редактор</TableCell>
                                        <TableCell>SLA</TableCell>
                                        <TableCell align="right">Штраф, %</TableCell>
                                        <TableCell align="right">Категорий</TableCell>
                                        <TableCell align="right">Подкатегорий</TableCell>
                                        <TableCell align="right">Файл</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {slaHistory.map((historyItem, index) => (
                                        <TableRow key={historyItem.id}>
                                            <TableCell>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <span>{formatDateTime(historyItem.createdAt)}</span>
                                                    {index === 0 ? (
                                                        <Chip size="small" color="success" variant="outlined" label="Актуальная" />
                                                    ) : null}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>{formatTariffHistoryActorLabel(historyItem.updatedBy)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    color={historyItem.slaEnabled !== false ? 'success' : 'default'}
                                                    variant={historyItem.slaEnabled !== false ? 'filled' : 'outlined'}
                                                    label={historyItem.slaEnabled !== false ? 'Включен' : 'Отключен'}
                                                />
                                            </TableCell>
                                            <TableCell align="right">{historyItem.penaltyPercent ?? 10}</TableCell>
                                            <TableCell align="right">{historyItem.categoryCount ?? 0}</TableCell>
                                            <TableCell align="right">{historyItem.subcategoryCount ?? 0}</TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={downloadingSlaRevisionId === historyItem.id ? <CircularProgress size={14} /> : <DownloadIcon />}
                                                    onClick={() => handleDownloadSlaRevisionPdf(historyItem)}
                                                    disabled={downloadingSlaRevisionId === historyItem.id}
                                                >
                                                    {downloadingSlaRevisionId === historyItem.id ? 'Формирую...' : 'Скачать PDF'}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">
                        Последние 30 проверенных объявлений
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID объявления</TableCell>
                                    <TableCell>Дата вердикта</TableCell>
                                    <TableCell>Вердикт</TableCell>
                                    <TableCell align="right">Тариф</TableCell>
                                    <TableCell>Эксперт</TableCell>
                                    <TableCell>Нарушение SLA</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stats.recentCheckedAds.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">Пусто</TableCell>
                                    </TableRow>
                                ) : stats.recentCheckedAds.map((item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>{item.adId || '-'}</TableCell>
                                        <TableCell>{formatDateTime(item.decidedAt)}</TableCell>
                                        <TableCell>{item.verdictLabel || '-'}</TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                                <span>{maskMoney(item.tariffRub, hideAmount)}</span>
                                                {item.slaViolated && item.slaApplicable !== false ? (
                                                    <Chip
                                                        size="small"
                                                        color="error"
                                                        variant="outlined"
                                                        label={`-${formatPercent(item.slaDiscountPercent ?? 10)}%`}
                                                    />
                                                ) : null}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{item.expertFullName !== '-' ? item.expertFullName : item.expertLogin}</TableCell>
                                        <TableCell>{renderSlaBadge(item.slaViolated, item.slaApplicable)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};
